import type {
	ActiveInlineCommentsQueryType,
	TopLevelComment,
	CommentLocation,
	CommentReply,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import { isValidUnreadComment } from '@confluence/unread-comments/entry-points/unreadCommentCountUtils';
import { PageMode, ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { fg } from '@confluence/feature-gating';
import type { ResolvedCommentsQueryType } from '@confluence/comments-panel-queries/entry-points/ResolvedCommentsQuery';
import type { ActiveCommentsQueryType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';

// eslint-disable-next-line import/no-cycle
import {
	CommentType,
	FooterCommentsLoadingStateType as FooterCommentsLoadingStateEnum,
	// eslint-disable-next-line import/no-cycle
} from '../useCommentsData';
import type {
	CommentData,
	CommentsDataMap,
	FooterCommentsLoadingStateType,
} from '../useCommentsData';

type NormalizeCommentDataFn = (input: {
	comment: TopLevelComment;
	readCommentsListState: Set<string>;
	currentUserId: string;
	annotationsInEditorDoc?: Set<string>;
	pageMode: PageMode;
	isOpen: boolean;
	type: CommentType;
}) => CommentData;

export const normalizeCommentData: NormalizeCommentDataFn = ({
	comment,
	readCommentsListState,
	currentUserId,
	annotationsInEditorDoc,
	pageMode,
	isOpen,
	type,
}) => {
	const isUnread = Boolean(
		// NOTE: we no longer use this isUnread field when fg('confluence_frontend_unread_comments_fixes') is enabled
		isValidUnreadComment({
			comment,
			viewedComments: readCommentsListState,
			userId: currentUserId,
			annotationsInEditorDoc,
			pageMode: pageMode as 'view' | 'edit',
		}),
	);

	return {
		...comment,
		isUnread,
		wasRemovedByAnotherUser: false,
		isOpen,
		type,
		replies: (comment.replies as CommentReply[])?.map((reply) => {
			if (!reply.reactionsSummary) {
				reply.reactionsSummary = null;
			}

			const isReplyUnread = Boolean(
				// NOTE: we no longer use this isUnread field when fg('confluence_frontend_unread_comments_fixes') is enabled
				isValidUnreadComment({
					comment: reply,
					viewedComments: readCommentsListState,
					userId: currentUserId,
					annotationsInEditorDoc,
					pageMode: pageMode as 'view' | 'edit',
				}),
			);

			return {
				...reply,
				type,
				isUnread: isReplyUnread,
				wasRemovedByAnotherUser: false,
			};
		}),
	};
};

export const updateCommentsDataState = ({
	data,
	readCommentsListState,
	annotationsInEditorDoc,
	pageMode,
	currentUserId,
	updateData,
	isOpen = true,
}: {
	data: ActiveInlineCommentsQueryType | ActiveCommentsQueryType | ResolvedCommentsQueryType;
	readCommentsListState: Set<string>;
	annotationsInEditorDoc?: Set<string>;
	pageMode: PageMode;
	currentUserId: string;
	updateData: (newCommentsDataMap: CommentsDataMap) => void;
	isOpen: boolean;
}) => {
	const inlineCommentThreadsToAdd: Record<string, CommentData> = {};
	const generalCommentThreadsToAdd: Record<string, CommentData> = {};

	const loadFooterComments = fg('confluence_comment_footercomments_editpage')
		? pageMode !== PageMode.EDIT
		: true;

	data?.comments?.nodes?.forEach((comment) => {
		// We only want top level comments
		if (comment && !comment.parentId) {
			if (!comment.reactionsSummary) {
				comment.reactionsSummary = null;
			}

			if (
				comment.contentStatus === ContentStatus.DRAFT &&
				pageMode === PageMode.VIEW &&
				fg('confluence_frontend_content_wrapper')
			) {
				return;
			}

			const commentType = comment.location.type;

			if (commentType === 'INLINE') {
				const threadKey = (comment.location as CommentLocation).inlineMarkerRef;

				if (threadKey) {
					inlineCommentThreadsToAdd[threadKey] = normalizeCommentData({
						comment,
						readCommentsListState,
						currentUserId,
						annotationsInEditorDoc,
						pageMode,
						isOpen,
						type: CommentType.INLINE,
					});
				}
			} else if (commentType === 'FOOTER' && loadFooterComments) {
				const threadKey = comment.id;

				generalCommentThreadsToAdd[threadKey] = normalizeCommentData({
					comment,
					readCommentsListState,
					currentUserId,
					pageMode,
					isOpen,
					type: CommentType.GENERAL,
				});
			}
		}
	});

	updateData({
		inline: inlineCommentThreadsToAdd,
		general: generalCommentThreadsToAdd,
	});
};

export const parseHasNestedComments = (commentsDataMap: CommentsDataMap) => {
	try {
		const allGeneralComments = Object.values(commentsDataMap.general);

		// To see if there are nested comments, we need to check whether there are any replies whose parentID is not equal to the parent Comment's ID
		// This is because commentsDataMap.general is a flat map of parent comment --> everything below it
		// But each comment has it's own parent id which may be different from it's top level parent
		return allGeneralComments.some((comment) => {
			return comment?.replies?.some((reply) => reply.parentId !== comment.id);
		});
	} catch {
		// Default to false if somehow something goes wrong
		return false;
	}
};

export const updateFooterCommentsLoadingState = (
	activeCommentsQueryResult: ActiveCommentsQueryType | undefined,
	updateFn: (state: FooterCommentsLoadingStateType) => void,
) => {
	// This should only ever be called with data - however if it isn't we should update state to NOT_LOADED
	if (activeCommentsQueryResult?.comments?.totalCount === undefined) {
		updateFn(FooterCommentsLoadingStateEnum.NOT_LOADED);
		return;
	}

	const loadedCommentCount =
		activeCommentsQueryResult?.comments?.nodes?.reduce((acc, comment) => {
			return acc + (comment?.replies?.length || 0) + 1; // return parent + reply count
		}, 0) ?? 0;

	// The BE sometimes will give us mismatched numbers for complex nested scenarios, as long as > 0 call it good
	// With the FG off this prevents rendering, so add a special case
	// With the FG on it renders properly, but load experience may fail to close
	// This case is super rare (should be <<1% of pages), but if it significantly affects metrics will need another special case
	const allLoaded =
		loadedCommentCount === activeCommentsQueryResult?.comments?.totalCount ||
		(!fg('confluence_frontend_always_ssr_footer_comments') && loadedCommentCount > 0);

	if (allLoaded) {
		updateFn(FooterCommentsLoadingStateEnum.FULLY_LOADED);
	} else if (fg('confluence_frontend_always_ssr_footer_comments')) {
		// partial load is only possible under this flag
		updateFn(FooterCommentsLoadingStateEnum.PARTIALLY_LOADED);
	} else {
		// If not partial or full, most be not loaded
		updateFn(FooterCommentsLoadingStateEnum.NOT_LOADED);
	}
};

export const findCommentById = (commentsDataMap: CommentsDataMap, commentId: string) => {
	// Check if our ID is one of the parent comments, if so return the parent comment
	const findParent = Object.values(commentsDataMap.general).find(
		(comment) =>
			comment.id === commentId || comment.replies?.some((reply) => reply.id === commentId),
	);

	if (!findParent) {
		return null;
	}

	if (findParent?.id === commentId) {
		return findParent;
	} else {
		return findParent.replies?.find((reply) => reply.id === commentId);
	}
};

// Returns the top level parent of a comment. If the comment Id supplied is not a reply, it'll return the comment in question
export const getTopLevelParentComment = (commentsDataMap: CommentsDataMap, commentId: string) => {
	return Object.values(commentsDataMap.general).find(
		(comment) =>
			comment.id === commentId || comment.replies?.some((reply) => reply.id === commentId),
	);
};
